import React, { useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { bindActionCreators } from 'redux'
import { connect as connectRedux } from 'react-redux'
import { compose } from 'recompose'
import { Map, List } from 'immutable'
import _parseInt from 'lodash/parseInt'
import _map from 'lodash/map'
import _omit from 'lodash/omit'
import _isEmpty from 'lodash/isEmpty'
import _unset from 'lodash/unset'
import { formatDateWithTimezonOffset } from 'services/date'
import _set from 'lodash/set'
import _get from 'lodash/get'
import { Formik, Form } from 'formik'
import Container from 'muicss/lib/react/container'
import Col from 'muicss/lib/react/col'
import Row from 'muicss/lib/react/row'
import Input from 'muicss/lib/react/input'
import Radio from 'muicss/lib/react/radio'
import Button from 'muicss/lib/react/button'
import Option from 'muicss/lib/react/option'
import Select from 'muicss-r18/lib/react/select'
import Loader from 'gui/components/common/Loader'
import { dismissModal as dismissModalAction } from 'services/modal/redux/actions'
import {
  createAmbassador as createAmbassadorAction,
  updateAmbassador as updateAmbassadorAction,
  getAmbassadorUserData as getAmbassadorUserDataAction,
  getAmbassadorByUtm as getAmbassadorByUtmAction,
} from 'services/ambassador/redux/actions'
import {
  AMBASSADOR_MAX_START_DATE,
  AMBASSADOR_RELATIONSHIPS,
  AMBASSADOR_ACTIVE,
  AMBASSADOR_INACTIVE,
  AMBASSADOR_STORE_KEY,
  AMBASSADOR_ADMIN_STORE_KEY,
  AMBASSADOR_USER_SEARCH_APPLICATION_KEY,
  AMBASSADOR_CREATE_APPLICATION_KEY,
  AMBASSADOR_UPDATE_APPLICATION_KEY,
  AMBASSADOR_PARENT_APPLICATION_KEY,
  getInitialFormValues,
  getValidationSchema,
} from 'services/ambassador'
import { getApplicationDataByKey, getViewRecordsAsList } from 'services/data'
import { COUNTRIES } from 'services/countries'
import { CURRENCIES } from 'services/currency'
import countries from 'i18n-iso-countries'
import './index.scss'

// Config to Eng by default
countries.registerLocale(require('i18n-iso-countries/langs/en.json'))

const renderRelationshipOptions = () =>
  AMBASSADOR_RELATIONSHIPS.map(({ name, label }) => (
    <Option key={name} value={name} label={label} />
  ))

const renderRelationshipOwnerOptions = (ambassadorAdmins) => {
  const legacy = ambassadorAdmins.find((a) => a.get('full_name') === 'Legacy Account')
  const sorted = ambassadorAdmins
    .sortBy((a) => a.get('full_name'))
    .filter((a) => a.get('full_name') !== 'Legacy Account')
  const admins = legacy ? sorted.push(legacy) : sorted
  return admins
    .map((admin) => (
      <Option key={admin.get('uuid')} value={admin.get('uuid')} label={admin.get('full_name')} />
    ))
    .unshift(<Option key={'unassigned'} value={null} label={'Unassigned'} />)
}

const renderCountryOptions = () =>
  _map(COUNTRIES, ({ code, name }) => <Option key={code} value={code} label={name} />)

const renderCurrencyOptions = () =>
  _map(CURRENCIES, (code) => <Option key={code} value={code} label={code} />)

/**
 * Component used to display a list of Ambassador payouts
 * @param {Map} props.ambassador The entity to edit
 * @param {function} props.dismissModal Action to close the modal
 * @returns Form to create/edit ambassadors
 */
const AmbassadorFormModal = (props) => {
  const {
    auth,
    config,
    editMode,
    ambassador,
    data,
    authUuid,
    dismissModal,
    createAmbassador,
    updateAmbassador,
    getUserData,
    getParentAmbassador,
  } = props
  const [isSubmitting, setIsSubmitting] = useState(false)
  const title = editMode ? 'Edit Partner' : 'Add New Partner'
  const titleCta = editMode ? 'Save Changes' : 'Create Partner'
  const userSearchData =
    getApplicationDataByKey({
      data,
      storeKey: AMBASSADOR_STORE_KEY,
      applicationKey: AMBASSADOR_USER_SEARCH_APPLICATION_KEY,
    }) || Map()
  const userUuid = userSearchData.getIn(['data', 'users', 0, 'uuid'])
  const userSearchProcessing = userSearchData.get('processing')
  const userSearchError = userSearchData.get('error')
  const userNotFound =
    userSearchData.getIn(['data', 'totalCount']) === 0 &&
    List.isList(userSearchData.getIn(['data', 'users'])) &&
    userSearchData.getIn(['data', 'users']).isEmpty()
  const ambassadorCreateData =
    getApplicationDataByKey({
      data,
      storeKey: AMBASSADOR_STORE_KEY,
      applicationKey: AMBASSADOR_CREATE_APPLICATION_KEY,
    }) || Map()
  const ambassadorCreateProcessing = ambassadorCreateData.get('processing')
  const ambassadorCreateError = ambassadorCreateData.get('error')
  const ambassadorParentData =
    getApplicationDataByKey({
      data,
      storeKey: AMBASSADOR_STORE_KEY,
      applicationKey: AMBASSADOR_PARENT_APPLICATION_KEY,
    }) || Map()
  const ambassadorParentProcessing = ambassadorParentData.get('processing')
  const ambassadorParentError = ambassadorParentData.get('error')
  const ambassadorReferralUuid = ambassadorParentData.getIn(['data', 'ambassadors', 0, 'uuid'])
  const ambassadorParentNotFound = ambassadorParentData.getIn(['data', 'count']) === 0
  const ambassadorUpdateData =
    getApplicationDataByKey({
      data,
      storeKey: AMBASSADOR_STORE_KEY,
      applicationKey: AMBASSADOR_UPDATE_APPLICATION_KEY,
    }) || Map()
  const ambassadorUpdateProcessing = ambassadorUpdateData.get('processing')
  const ambassadorUpdateError = ambassadorUpdateData.get('error')
  const warningMessage = (
    <span className="warning">
      Confirm this is correct. You will not be able to modify this value later.
    </span>
  )

  const ambassadorAdmins =
    getViewRecordsAsList({
      data,
      storeKey: AMBASSADOR_ADMIN_STORE_KEY,
    }) || List()

  const renderLoader = () => {
    if (
      isSubmitting ||
      userSearchProcessing ||
      ambassadorCreateProcessing ||
      ambassadorParentProcessing ||
      ambassadorUpdateProcessing
    ) {
      return (
        <div className="ambassador-form-modal__loader">
          <Loader />
        </div>
      )
    }

    return null
  }

  const renderError = (field, touched, errors, setFieldError, values) => {
    if (touched[field] && errors[field]) {
      return <div className="error">{errors[field]}</div>
    }

    if (touched[field] && field === 'gaiaEmail' && userNotFound) {
      return setFieldError('gaiaEmail', 'Gaia Account email does not exist.')
    }

    if (touched[field] && field === 'gaiaEmail' && userSearchError) {
      return setFieldError(
        'gaiaEmail',
        'Server error: there was a problem verifying the email address. Try again later.',
      )
    }

    if (touched[field] && field === 'parentAmbassadorUuid' && values.parentAmbassadorUuid) {
      if (ambassadorParentError)
        return setFieldError(
          'parentAmbassadorUuid',
          'Server error: there was a problem verifying the UTM.',
        )
      if (ambassadorParentNotFound)
        return setFieldError('parentAmbassadorUuid', 'Gaia Partner does not exist.')
      if (ambassadorParentData.getIn(['data', 'ambassadors', 0, 'utmMedium']) === values.utmMedium)
        return setFieldError('parentAmbassadorUuid', 'Referring Partner cannot be self.')
      if (
        ambassadorParentData.getIn(['data', 'ambassadors', 0, 'parentAmbassador', 'utmMedium']) ===
        values.utmMedium
      )
        return setFieldError('parentAmbassadorUuid', 'Inverse relationship already exists.')
    }

    return null
  }

  const renderUtmMediumWarning = (touched, values, errors) => {
    if (!editMode && touched.utmMedium && values.utmMedium && !errors.utmMedium) {
      return warningMessage
    }

    return null
  }

  const renderGaiaEmailWarning = (touched, values, errors) => {
    if (!editMode && touched.gaiaEmail && values.gaiaEmail && !errors.gaiaEmail) {
      return warningMessage
    }

    return null
  }

  const getNormalizedValues = (values, edit = false) => {
    let normalized = {
      ...values,
      isActive: !!_parseInt(values.isActive),
      userInfoUuid: userUuid,
      residenceCountry: countries.alpha2ToAlpha3(values.residenceCountry),
      relationshipOwnerUuid: values.relationshipOwner,
    }
    const parentAmbassadorUuid = _get(normalized, 'parentAmbassadorUuid')
    if (_isEmpty(parentAmbassadorUuid)) _unset(normalized, 'parentAmbassadorUuid')
    const gaiaEmail = _get(normalized, 'gaiaEmail')
    const startedAt = _get(normalized, 'startedAt')
    const relationshipOwnerUuid = _get(normalized, 'relationshipOwnerUuid')

    // set the ambassadorReferralUuid if it exists
    // else if editMode, get the value from the ambassador or ambassadorReferralUuid
    if (!edit && parentAmbassadorUuid && ambassadorReferralUuid) {
      normalized = _set(normalized, 'parentAmbassadorUuid', ambassadorReferralUuid)
    } else if (edit) {
      let parentUuid = ambassador.get('parentAmbassadorUuid')

      if (
        parentAmbassadorUuid &&
        ambassadorReferralUuid &&
        ambassadorReferralUuid !== ambassador.get('parentAmbassadorUuid')
      ) {
        parentUuid = ambassadorReferralUuid
      } else if (ambassador.get('parentAmbassadorUuid') && !parentAmbassadorUuid) {
        parentUuid = null
      }

      normalized = _set(normalized, 'parentAmbassadorUuid', parentUuid)
    }

    // we should never send the gaiaEmail
    if (gaiaEmail) {
      normalized = _omit(normalized, 'gaiaEmail')
    }

    // change date format
    if (startedAt) {
      const date = new Date(startedAt)
      normalized = _set(normalized, 'startedAt', formatDateWithTimezonOffset(date))
    }

    // relationship owner
    if (relationshipOwnerUuid === 'Unassigned') {
      normalized = _set(normalized, 'relationshipOwnerUuid', null)
    }

    // if in editMode, we do not send the userInfoUuid
    if (edit) {
      normalized = _omit(normalized, 'userInfoUuid')
    }

    return normalized
  }

  const onSubmitHandler = (values) => {
    if (!editMode && !userUuid) {
      return
    }

    setIsSubmitting(true)

    if (editMode) {
      updateAmbassador(getNormalizedValues(values, true), ambassador.get('uuid'))
    } else {
      createAmbassador(getNormalizedValues(values))
    }

    setIsSubmitting(false)
  }

  const onDismissHandler = (e) => {
    e.persist()
    dismissModal()
  }

  return (
    <div className="ambassador-form-modal">
      <h2 className="ambassador-form-modal__title">{title}</h2>
      {renderLoader()}
      {(ambassadorCreateError || ambassadorUpdateError) && (
        <div className="ambassador-form-modal__error">There was an error with the API</div>
      )}
      <Formik
        initialValues={getInitialFormValues(ambassador, authUuid, ambassadorAdmins)}
        validationSchema={getValidationSchema(auth, config, editMode)}
        onSubmit={onSubmitHandler}
        className="ambassador-form-modal__form"
      >
        {({ values, errors, touched, isValid, dirty, handleChange, handleBlur, setFieldError }) => (
          <Form>
            <Container className="ambassador-form-modal__container" fluid={true}>
              <div>
                <Row>
                  <Col sm="12">
                    <Input
                      className="ambassador-form-modal__businessName"
                      label="Business Name"
                      name="businessName"
                      value={values.businessName || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.businessName && errors.businessName}
                    />
                    {renderError('businessName', touched, errors)}
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Input
                      className="ambassador-form-modal__name"
                      label="Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.name && errors.name}
                    />
                    {renderError('name', touched, errors)}
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Select
                      label="Relationship"
                      name="relationship"
                      onChange={handleChange}
                      value={values.relationship}
                    >
                      {renderRelationshipOptions()}
                    </Select>
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Input
                      className="ambassador-form-modal__email"
                      label="Contact Email Address"
                      name="contactEmail"
                      value={values.contactEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.contactEmail && errors.contactEmail}
                    />
                    {renderError('contactEmail', touched, errors)}
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Input
                      className="ambassador-form-modal__gaia-email"
                      label="Gaia Account Email / RFD Email"
                      name="gaiaEmail"
                      value={values.gaiaEmail}
                      onChange={(e) => {
                        handleChange(e)
                      }}
                      onBlur={(e) => {
                        handleBlur(e)
                        if (!editMode && !!e.target.value) {
                          getUserData(e.target.value)
                        }
                      }}
                      invalid={!editMode && touched.gaiaEmail && errors.gaiaEmail}
                      readOnly={editMode}
                    />
                    {renderGaiaEmailWarning(touched, values, errors)}
                    {renderError('gaiaEmail', touched, errors, setFieldError)}
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Input
                      className="ambassador-form-modal__utm-medium"
                      label="UTM_Medium"
                      name="utmMedium"
                      value={values.utmMedium}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={!editMode && touched.utmMedium && errors.utmMedium}
                      readOnly={editMode}
                    />
                    {renderUtmMediumWarning(touched, values, errors)}
                    {renderError('utmMedium', touched, errors)}
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Input
                      className="ambassador-form-modal__everflowAffiliateId not-arrows"
                      label="Everflow Affiliate Id"
                      name="everflowAffiliateId"
                      type="number"
                      value={values.everflowAffiliateId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.everflowAffiliateId && errors.everflowAffiliateId}
                    />
                    {renderError('everflowAffiliateId', touched, errors)}
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Input
                      className="ambassador-form-modal__commission-ambassador not-arrows"
                      label="% Commission: Partner"
                      name="ripple1Percentage"
                      type="number"
                      value={values.ripple1Percentage}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.ripple1Percentage && errors.ripple1Percentage}
                    />
                    {renderError('ripple1Percentage', touched, errors)}
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Input
                      className="ambassador-form-modal__commission-agent not-arrows"
                      label="% Commission: Referral Agent"
                      name="ripple2Percentage"
                      type="number"
                      value={values.ripple2Percentage}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.ripple2Percentage && errors.ripple2Percentage}
                    />
                    {renderError('ripple2Percentage', touched, errors)}
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <div className="mui-textfield">
                      <span className="custom-label">Partner Start Date</span>
                      <input
                        type="date"
                        className="ambassador-form-modal__start-date"
                        name="startedAt"
                        value={values.startedAt}
                        onChange={handleChange}
                        max={AMBASSADOR_MAX_START_DATE}
                      />
                    </div>
                    {renderError('startedAt', touched, errors)}
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <div className="mui-textfield">
                      <span className="custom-label">Active</span>
                      <div className="ambassador-form-modal__active">
                        <Radio
                          name="isActive"
                          value={AMBASSADOR_ACTIVE}
                          label="Yes"
                          checked={values.isActive === AMBASSADOR_ACTIVE}
                          onChange={handleChange}
                        />
                        <Radio
                          name="isActive"
                          value={AMBASSADOR_INACTIVE}
                          label="No"
                          checked={values.isActive === AMBASSADOR_INACTIVE}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Select
                      className="ambassador-form-modal__country"
                      name="residenceCountry"
                      label="Country of Residence"
                      value={values.residenceCountry}
                      onChange={handleChange}
                    >
                      {renderCountryOptions()}
                    </Select>
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Select
                      className="ambassador-form-modal__currency"
                      name="payoutCurrency"
                      label="Payout Currency"
                      value={values.payoutCurrency}
                      onChange={handleChange}
                    >
                      {renderCurrencyOptions()}
                    </Select>
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Input
                      className="ambassador-form-modal__ref-utm-medium"
                      label="Referring Partner UTM_Medium (optional)"
                      name="parentAmbassadorUuid"
                      value={values.parentAmbassadorUuid || ''}
                      onChange={(e) => {
                        handleChange(e)
                      }}
                      onBlur={(e) => {
                        handleBlur(e)
                        if (e.target.value) {
                          getParentAmbassador(e.target.value)
                        }
                      }}
                      invalid={
                        values.parentAmbassadorUuid &&
                        touched.parentAmbassadorUuid &&
                        errors.parentAmbassadorUuid
                      }
                    />
                    {renderError('parentAmbassadorUuid', touched, errors, setFieldError, values)}
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Select
                      label="Relationship Owner"
                      name="relationshipOwner"
                      onChange={handleChange}
                      value={values.relationshipOwner}
                    >
                      {renderRelationshipOwnerOptions(ambassadorAdmins)}
                    </Select>
                  </Col>
                </Row>
                <Row>
                  <Col sm="12">
                    <Button variant="raised" type="submit" disabled={!(isValid && dirty)}>
                      {titleCta}
                    </Button>
                    <Button
                      type="button"
                      variant="raised"
                      color="accent"
                      className="mui-btn--secondary"
                      onClick={onDismissHandler}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </div>
            </Container>
          </Form>
        )}
      </Formik>
    </div>
  )
}

AmbassadorFormModal.propTypes = {
  editMode: PropTypes.bool,
  data: ImmutablePropTypes.map.isRequired,
  ambassador: ImmutablePropTypes.map,
  dismissModal: PropTypes.func.isRequired,
  createAmbassador: PropTypes.func.isRequired,
  updateAmbassador: PropTypes.func.isRequired,
  getUserData: PropTypes.func.isRequired,
  getParentAmbassador: PropTypes.func.isRequired,
}

AmbassadorFormModal.defaultProps = {
  editMode: false,
  ambassador: Map(),
}

export default compose(
  connectRedux(
    (state) => ({
      auth: state.auth,
      config: state.config,
      data: state.data,
      authUuid: state.auth.getIn(['data', 'uuid']),
    }),
    (dispatch) =>
      bindActionCreators(
        {
          dismissModal: dismissModalAction,
          createAmbassador: createAmbassadorAction,
          updateAmbassador: updateAmbassadorAction,
          getUserData: getAmbassadorUserDataAction,
          getParentAmbassador: getAmbassadorByUtmAction,
        },
        dispatch,
      ),
  ),
)(AmbassadorFormModal)
